import React from 'react';

const HorseAnimation = ({ surplus, size = 'medium', label = 'Horse', thresholds = { stationary: 0.3, walking: 1.0 } }) => {
    // Determine animation state based on surplus and custom thresholds
    const getAnimationState = (surplus) => {
        if (surplus <= thresholds.stationary) {
            return 'stationary';
        } else if (surplus > thresholds.stationary && surplus < thresholds.walking) {
            return 'walking';
        } else {
            return 'running';
        }
    };

    const animationState = getAnimationState(surplus);
    
    // Size classes
    const sizeClasses = {
        small: 'w-16 h-16',
        medium: 'w-24 h-24',
        large: 'w-32 h-32'
    };

    // Animation styles
    const getAnimationStyle = (state) => {
        switch (state) {
            case 'stationary':
                return {
                    animation: 'none',
                    transform: 'translateX(0)'
                };
            case 'walking':
                return {
                    animation: 'horseWalk 1.5s ease-in-out infinite',
                    transform: 'translateX(0)'
                };
            case 'running':
                return {
                    animation: 'horseRun 0.6s linear infinite',
                    transform: 'translateX(0)'
                };
            default:
                return {};
        }
    };

    return (
        <>
            <style jsx>{`
                @keyframes horseWalk {
                    0% { 
                        transform: translateX(0) rotate(0deg) translateY(0);
                    }
                    25% { 
                        transform: translateX(3px) rotate(2deg) translateY(-1px);
                    }
                    50% { 
                        transform: translateX(0) rotate(0deg) translateY(0);
                    }
                    75% { 
                        transform: translateX(-3px) rotate(-2deg) translateY(-1px);
                    }
                    100% { 
                        transform: translateX(0) rotate(0deg) translateY(0);
                    }
                }
                
                @keyframes horseRun {
                    0% { 
                        transform: translateX(0) rotate(0deg) translateY(0) scale(1);
                    }
                    8% { 
                        transform: translateX(2px) rotate(1deg) translateY(-2px) scale(1.02);
                    }
                    16% { 
                        transform: translateX(4px) rotate(2deg) translateY(-4px) scale(1.05);
                    }
                    24% { 
                        transform: translateX(6px) rotate(3deg) translateY(-6px) scale(1.08);
                    }
                    32% { 
                        transform: translateX(8px) rotate(4deg) translateY(-8px) scale(1.1);
                    }
                    40% { 
                        transform: translateX(6px) rotate(3deg) translateY(-6px) scale(1.08);
                    }
                    48% { 
                        transform: translateX(4px) rotate(2deg) translateY(-4px) scale(1.05);
                    }
                    56% { 
                        transform: translateX(2px) rotate(1deg) translateY(-2px) scale(1.02);
                    }
                    64% { 
                        transform: translateX(0) rotate(0deg) translateY(0) scale(1);
                    }
                    72% { 
                        transform: translateX(-2px) rotate(-1deg) translateY(-2px) scale(1.02);
                    }
                    80% { 
                        transform: translateX(-4px) rotate(-2deg) translateY(-4px) scale(1.05);
                    }
                    88% { 
                        transform: translateX(-6px) rotate(-3deg) translateY(-6px) scale(1.08);
                    }
                    96% { 
                        transform: translateX(-8px) rotate(-4deg) translateY(-8px) scale(1.1);
                    }
                    100% { 
                        transform: translateX(0) rotate(0deg) translateY(0) scale(1);
                    }
                }

                @keyframes horseLegs {
                    0% { 
                        transform: rotate(0deg);
                    }
                    25% { 
                        transform: rotate(5deg);
                    }
                    50% { 
                        transform: rotate(0deg);
                    }
                    75% { 
                        transform: rotate(-5deg);
                    }
                    100% { 
                        transform: rotate(0deg);
                    }
                }

                @keyframes horseBodyBounce {
                    0% { 
                        transform: translateY(0) scaleY(1);
                    }
                    25% { 
                        transform: translateY(-2px) scaleY(1.02);
                    }
                    50% { 
                        transform: translateY(0) scaleY(1);
                    }
                    75% { 
                        transform: translateY(-1px) scaleY(1.01);
                    }
                    100% { 
                        transform: translateY(0) scaleY(1);
                    }
                }

                .horse-running {
                    animation: horseRun 0.6s linear infinite;
                }

                .horse-legs {
                    animation: horseLegs 0.3s ease-in-out infinite;
                }

                .horse-body-bounce {
                    animation: horseBodyBounce 0.4s ease-in-out infinite;
                }
            `}</style>
            
            <div className="flex items-center justify-end relative">
                {/* Status indicator - positioned to the left */}
                <div className={`w-6 h-6 rounded-full mr-3 ${
                    animationState === 'stationary' ? 'bg-gray-400' :
                    animationState === 'walking' ? 'bg-yellow-400 animate-pulse' :
                    'bg-green-400 animate-ping'
                } opacity-75`}></div>
                
                {/* Enhanced Horse with realistic running animation */}
                <div 
                    className={`${sizeClasses[size]} flex items-center justify-center cursor-pointer relative`}
                    style={getAnimationStyle(animationState)}
                    title={`${label}: ${animationState} (Surplus: ${surplus} kW)`}
                >
                    {/* Main horse body */}
                    <div className={`relative ${animationState === 'running' ? 'horse-body-bounce' : ''}`}>
                        <span className="text-6xl">🐎</span>
                        
                        {/* Running dust/particles effect */}
                        {animationState === 'running' && (
                            <>
                                <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-yellow-300 rounded-full opacity-60 animate-ping"></div>
                                <div className="absolute -bottom-1 -left-4 w-1 h-1 bg-yellow-400 rounded-full opacity-40 animate-ping" style={{animationDelay: '0.2s'}}></div>
                                <div className="absolute -bottom-3 -left-6 w-1 h-1 bg-yellow-500 rounded-full opacity-30 animate-ping" style={{animationDelay: '0.4s'}}></div>
                            </>
                        )}
                        
                        {/* Speed lines for running effect */}
                        {animationState === 'running' && (
                            <>
                                <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-gray-400 opacity-60 transform -rotate-12"></div>
                                <div className="absolute top-1/2 -right-10 w-4 h-0.5 bg-gray-500 opacity-40 transform -rotate-8" style={{animationDelay: '0.1s'}}></div>
                                <div className="absolute top-1/2 -right-12 w-3 h-0.5 bg-gray-600 opacity-30 transform -rotate-6" style={{animationDelay: '0.2s'}}></div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default HorseAnimation;
